// ── SDMS ribbon "Report" — live-sheet data extraction (OG BindSampleToReport) ──────────
// OG (SpreadsheetEntryController.cs :17919) builds the raw-data report's table straight off
// the LOADED spreadsheet control, not from the saved rows:
//   • start row  = the row of the RawDataTable field "SampleID" (:17928/:17933);
//   • BatchSampleEntry (Model 0): ONE rectangle A{start}:{lastUsedCol}{lastUsedRow} of the
//     band sheet (:18040-18058) — every column, formulas included, blank cells → NULL;
//   • SampleSheetNavigation (Model 1): the same rectangle from EACH per-run sheet (named by
//     SysSampleCode), rows appended in run order (:17945-18035);
//   • header cells: SpreadSheetBuilder_Header positions read from the (last) worksheet
//     (:18078-18118) and merged onto every row under their captions.
// The server (SdmsReportService) names the columns from SpreadSheetBuilder_Detail, types the
// dates, appends the batch id + signature images and renders the DevExpress layout.  Keeping
// the extraction pure (grids in, rows out) lets it be unit-tested without Univer.

/** "Sheet Name!AB12" → { sheet, row, col } (0-based). Null on anything unparseable. */
export function parseSheetRef(ref: string | undefined | null): { sheet: string; row: number; col: number } | null {
  if (!ref) return null;
  const bang = ref.lastIndexOf('!');
  const sheet = bang >= 0 ? ref.slice(0, bang) : '';
  const a1 = ref.slice(bang + 1);
  const m = /^\$?([A-Z]+)\$?(\d+)$/.exec(a1.trim().toUpperCase());
  if (!m) return null;
  let col = 0;
  for (const ch of m[1]!) col = col * 26 + (ch.charCodeAt(0) - 64);
  return { sheet, row: parseInt(m[2]!, 10) - 1, col: col - 1 };
}

/** Live cell values: numbers are kept typed (the header-cell rule below needs to know a
 *  NUMERIC zero from the text "0"); everything else is text or null (blank). */
export type SheetGrid = readonly (readonly (string | number | null)[])[];

export interface SdmsReportExtractInput {
  /** The merge's FieldName → "Sheet!A1" map (upper-cased keys). */
  fieldRefs: Readonly<Record<string, string>>;
  /** SpreadSheetBuilder_TemplateInfo.Model: 0 = one band sheet, 1 = one sheet per run. */
  model: number;
  /** Model 1 only: the run sheet names in sequence order (SysSampleCodes). */
  runCodes: readonly string[];
  /** SpreadSheetBuilder_Header positions (A1, no sheet) whose cells feed the header columns. */
  headerPositions: readonly string[];
  /** The live sheet's used-range values (trailing blank rows/cols may be present). */
  readSheet: (sheetName: string) => SheetGrid | null;
}

export interface SdmsReportSheetData {
  rows: (string | null)[][];
  headerCells: Record<string, string | null>;
}

const blank = (v: string | number | null | undefined): boolean => v == null || String(v).trim() === '';

/** OG header-cell rule (BindSampleToReport :18106-18115): a TEXT cell is taken as-is; a NUMERIC
 *  cell is taken only when its value is > 0 — a numeric 0 / negative header cell stays NULL. */
export function headerCellValue(v: string | number | null | undefined): string | null {
  if (blank(v)) return null;
  if (typeof v === 'number') return v > 0 ? String(v) : null;
  return String(v);
}

/** Drop trailing all-blank rows and columns (DevExpress GetDataRange semantics). */
export function trimGrid(grid: SheetGrid): (string | null)[][] {
  let lastRow = -1, lastCol = -1;
  grid.forEach((r, ri) => r.forEach((v, ci) => { if (!blank(v)) { lastRow = Math.max(lastRow, ri); lastCol = Math.max(lastCol, ci); } }));
  const out: (string | null)[][] = [];
  for (let r = 0; r <= lastRow; r++) {
    const row: (string | null)[] = [];
    for (let c = 0; c <= lastCol; c++) { const v = grid[r]?.[c]; row.push(blank(v) ? null : String(v)); }
    out.push(row);
  }
  return out;
}

/** The band anchor: the SampleID field's row/sheet, else SysSampleCode's (OG uses SampleID;
 *  a template without it would NRE in OG — the fallback keeps AI usable). Null when neither. */
export function bandAnchor(fieldRefs: Readonly<Record<string, string>>): { sheet: string; row: number } | null {
  const pick = (k: string) => parseSheetRef(fieldRefs[k]);
  const a = pick('SAMPLEID') ?? pick('SYSSAMPLECODE');
  return a ? { sheet: a.sheet, row: a.row } : null;
}

/** Build the report's sheet data (rows + header cells) from the live grids. Null when the band
 *  anchor is missing or no sheet could be read. */
export function extractSdmsReportData(input: SdmsReportExtractInput): SdmsReportSheetData | null {
  const anchor = bandAnchor(input.fieldRefs);
  if (!anchor) return null;

  const sheetsToRead = input.model === 1 && input.runCodes.length > 0 ? input.runCodes : [anchor.sheet];
  const rows: (string | null)[][] = [];
  let lastGrid: (string | null)[][] | null = null;
  let lastHeaderGrid: SheetGrid | null = null;   // typed (numbers kept) for the header-cell rule
  let width = 0;
  for (const name of sheetsToRead) {
    const raw = input.readSheet(name);
    if (!raw) continue;
    const grid = trimGrid(raw);
    lastGrid = grid; lastHeaderGrid = raw;
    width = Math.max(width, grid[0]?.length ?? 0);
    for (let r = anchor.row; r < grid.length; r++) rows.push(grid[r]!);
  }
  if (!lastGrid) return null;
  // Pad to a rectangle (OG's DataTable has one column set for all rows).
  for (const r of rows) while (r.length < width) r.push(null);

  // Header cells come from the (last-read) worksheet, OG :18084 — Model 0 = the band sheet.
  const headerCells: Record<string, string | null> = {};
  for (const pos of input.headerPositions) {
    const ref = parseSheetRef(pos);
    if (!ref) continue;
    headerCells[pos.trim().toUpperCase()] = headerCellValue(lastHeaderGrid?.[ref.row]?.[ref.col]);
  }
  return { rows, headerCells };
}
